import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from '../shared/Button';
import TextFieldWithCounter from '../shared/TextFieldWithCounter';
import { isValidDocumentTitle, isValidTitleLength } from '../../utils/validators';
import { Toast } from '../shared/Toast';

const styles = {
	section: 'fixed inset-0 z-[9999] flex items-center justify-center p-4',
	backDrop: 'absolute inset-0 bg-black/70',
	modal: 'relative z-10 w-full max-w-[500px] rounded-[18px] bg-white shadow-2xl p-[30px] max-h-[90vh] no-scrollbar',
	heading: 'text-25 font-bold text-dark-blue mb-1 leading-100',
	subHeading: 'text-gray text-sm xl:mb-6 md:mb-2',
	label: 'text-base leading-100 font-medium text-dark-blue',
};

export default function RenameDocumentModal({ 
	isOpen, 
	onClose, 
	currentTitle = '', 
	onRename 
}) {
	const [documentTitle, setDocumentTitle] = useState(currentTitle);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const resetModalState = () => {
		setDocumentTitle(currentTitle);
		setIsSubmitting(false);
	};
	useEffect(() => {
    if (isOpen) {
      setDocumentTitle(currentTitle);
      setIsSubmitting(false);
    }
  }, [isOpen, currentTitle]);
	const handleClose = () => {
		resetModalState();
		if (typeof onClose === 'function') onClose();
	};

	// const handleBackdropClick = (e) => {
	// 	if (e.target === e.currentTarget) handleClose();
	// };

	const handleRenameDocument = async () => {
		try {
			setIsSubmitting(true);

			// Document title validation
			if (!documentTitle.trim()) {
				Toast.error('Please enter your document title.');
				setIsSubmitting(false);
				return;
			}
			if (!isValidTitleLength(documentTitle)) {
				Toast.error('Document title must be between 3 and 75 characters.');
				setIsSubmitting(false);
				return;
			}
			if (!isValidDocumentTitle(documentTitle)) {
				Toast.error('Document title can only include letters');
				setIsSubmitting(false);
				return;
			}

			if (typeof onRename === 'function') {
				await onRename(documentTitle);
			}
			
			handleClose();
		} catch (error) {
			const message = error?.response?.data?.message || error?.message || 'Failed to rename document';
			Toast.error(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className={styles.section}>
			{/* Backdrop */}
			<div className={styles.backDrop} />

			{/* Modal */}
			<div className={styles.modal}>
				<button
					onClick={handleClose}
					className="absolute top-8 right-2 md:right-6 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
				>
					<X className="w-3 h-3 text-white" />
				</button>

				{/* Heading */}
				<h2 className={styles.heading}>Rename Document</h2>
				<p className={styles.subHeading}>Enter a new name for your document</p>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />
				
				{/* Input field */}
				<div className="grid grid-cols-1 gap-6 xl:mb-4 mb-2">
					<div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="document-title" className={styles.label}>
								Document title <span className="text-red-500">*</span>
							</label>
						</div>
						<TextFieldWithCounter
							className='focus:outline-none focus:border-purple'
							id="document-title"
							placeholder="Enter document title"
							value={documentTitle}
							onChange={(e) => setDocumentTitle(e.target.value)}
							minLength={3}
							maxLength={75}
						/>
					</div>
				</div>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				{/* Buttons */}
				<div className="flex xl:gap-3 gap-2">
					<Button 
						onClick={handleClose}
						variant="outline-gradient-text"
						className="flex-1 h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] text-[16px] sm:text-[18px]"
					>
						Cancel
					</Button>
					<Button 
						onClick={handleRenameDocument} 
						disabled={isSubmitting || !documentTitle || documentTitle === currentTitle}
						className="flex-1 h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] text-white text-[16px] sm:text-[18px]"
					>
						{isSubmitting ? 'Saving...' : 'Save Name'}
					</Button>
				</div>
			</div>
		</div>
	);
}
